import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Check,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { Button } from '../components/ui/button.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.tsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown.tsx';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../components/ui/avatar.tsx';
import { Checkbox } from '../components/ui/checkbox.tsx';
import { Textarea } from '../components/ui/textarea.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import ClassModal from '../components/class/ClassModal';
import ClassScheduleOverview from '../components/class/ClassScheduleOverview.jsx';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import StudentRowData from '../components/class/StudentRowData.jsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover.tsx';
import AttendanceRowData from '../components/class/AttendanceRowData.jsx';
import { cn } from '../lib/utils.ts';
import { Calendar } from '../components/ui/calendar.tsx';
import StudentRecordRowData from '../components/class/StudentRecordRowData.jsx';

import { useGetAllClasses, useGetClassById } from '../api/class/classQueries';
import { useGetAllRecordTypes } from '../api/recordTypes/recordTypesQueries.js';
import { useGetClassEnrollments } from '../api/enrollment/enrollmentQueries.js';

export default function ClassPage() {
  const { classId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [classSchedule, setClassSchedule] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState();
  const [filteredEnrollments, setFilteredEnrollments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [markAllAttendance, setMarkAllAttendance] = useState(false);
  const [assignmentData, setAssignmentData] = useState([
    {
      id: 1,
      name: 'Temitope Toluwani',
      grade: 0,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
    {
      id: 2,
      name: 'Temitope Toluwani',
      grade: 0,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
    {
      id: 3,
      name: 'Temitope Toluwani',
      grade: 0,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
    {
      id: 4,
      name: 'Temitope Toluwani',
      grade: 0,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
  ]);
  const [attendanceData, setAttendanceData] = useState([
    {
      id: 1,
      name: 'Temitope Toluwani',
      present: false,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
    {
      id: 2,
      name: 'Temitope Toluwani',
      present: false,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
    {
      id: 3,
      name: 'Temitope Toluwani',
      present: false,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
    {
      id: 4,
      name: 'Temitope Toluwani',
      present: false,
      comment: '',
      avatar: '/placeholder.svg?height=40&width=40',
    },
  ]);

  const {
    data: EnrollmentData,
    isLoading: isEnrollmentDataLoading,
    isError: isEnrollmentDataError,
  } = useGetClassEnrollments(classId);

  const {
    data: classData,
    isLoading: isClassLoading,
    isError: isClassError,
  } = useGetClassById(classId);

  const {
    data: recordTypesData,
    isLoading: isRecordTypesLoading,
    isError: isRecordTypesError,
  } = useGetAllRecordTypes();

  // Handle mark all attendance change
  const handleMarkAllAttendance = (checked) => {
    setMarkAllAttendance(checked);
    setAttendanceData(
      attendanceData.map((student) => ({
        ...student,
        present: checked,
      }))
    );
  };

  // Handle attendance change
  const handleAttendanceChange = (id, checked) => {
    setAttendanceData(
      attendanceData.map((student) => {
        if (student.id === id) {
          return { ...student, present: checked };
        }
        return student;
      })
    );
    // Update markAllAttendance state based on all checkboxes
    const allChecked = attendanceData.every((student) =>
      student.id === id ? checked : student.present
    );
    setMarkAllAttendance(allChecked);
  };

  // Handle comment change
  const handleCommentChange = (id, comment) => {
    setAttendanceData(
      attendanceData.map((student) => {
        if (student.id === id) {
          return { ...student, comment };
        }
        return student;
      })
    );
  };

  // Handle grade change
  const handleGradeChange = (id, grade) => {
    setAssignmentData(
      assignmentData.map((student) => {
        if (student.id === id) {
          return { ...student, grade: Math.min(100, Math.max(0, grade)) };
        }
        return student;
      })
    );
  };

  // Handle comment change for assignments
  const handleAssignmentCommentChange = (id, comment) => {
    setAssignmentData(
      assignmentData.map((student) => {
        if (student.id === id) {
          return { ...student, comment };
        }
        return student;
      })
    );
  };

  // Open modal to create a new class
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!isEnrollmentDataLoading && !isEnrollmentDataError) {
      const uniqueSubjectsMap = new Map();
      EnrollmentData.data.forEach((enrollment) => {
        const subjectId = enrollment.subject.id;
        uniqueSubjectsMap.set(subjectId, {
          id: subjectId,
          subjectName: enrollment.subject.subjectName,
        });
      });
      setSubjects(Array.from(uniqueSubjectsMap.values()));
    }
  }, [isEnrollmentDataError, isEnrollmentDataLoading, EnrollmentData]);

  useEffect(() => {
    if (!isEnrollmentDataLoading && !isEnrollmentDataError) {
      // Todo: Filter based on active session as well.
      if (selectedSubject) {
        const filtered = EnrollmentData.data.filter(
          (enrollment) => enrollment.subject.id === selectedSubject
        );
        setFilteredEnrollments(filtered);
        console.log('Selected Subject: ', selectedSubject);
        console.log('Filtered Enrollment ', filtered);
      } else {
        setFilteredEnrollments(EnrollmentData.data);
      }
    }
  }, [
    isEnrollmentDataError,
    isEnrollmentDataLoading,
    selectedSubject,
    EnrollmentData,
  ]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {isClassLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-blue-600 border-r-transparent"></div>
          <p className="ml-2">Loading classes...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Students and Class Info */}
            <div className="bg-white rounded-lg p-4 mb-3">
              <div className="flex md:items-center md:justify-between md:flex-row flex-col w-full gap-y-4">
                <div className="flex items-center">
                  <div className="flex -space-x-4">
                    {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                      <div
                        key={index}
                        className={`${
                          index === 7
                            ? 'bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-medium'
                            : ''
                        } w-10 h-10 rounded-full border-2 border-white overflow-hidden`}
                      >
                        {index < 7 ? (
                          <img
                            src={`/placeholder.svg`}
                            alt={`Student ${index}`}
                            className="w-10 h-10 object-cover"
                          />
                        ) : (
                          <span>+5</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <span className="ml-4 font-semibold text-lg">
                    {classData.data.className}
                  </span>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  + Add Student
                </Button>
              </div>
            </div>

            {/* Date Selection and Subject Selection */}
            <div className="bg-white rounded-lg p-4 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={'outline'}
                        className={cn(
                          'justify-start text-left border-purple-200 text-purple-700'
                        )}
                      >
                        {date ? (
                          format(date, 'do, LLLL')
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side="bottom"
                    >
                      <Calendar
                        mode="single"
                        selected={new Date()}
                        onSelect={(date) => {
                          if (date) {
                            setDate(date);
                            setCalendarOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Button
                    className="bg-purple-500 hover:bg-purple-600"
                    onClick={() => setDate(new Date())}
                  >
                    Goto Today
                  </Button>
                </div>

                {isEnrollmentDataError ? (
                  <div>
                    <p className="text-red-500">Error loading subjects</p>
                  </div>
                ) : (
                  <div>
                    {isEnrollmentDataLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <Select
                        value={selectedSubject}
                        onValueChange={setSelectedSubject}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select Subject">
                            {selectedSubject?.subjectName}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {/* <SelectItem value="Mathematics">Mathematics</SelectItem>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Physics">Physics</SelectItem>
                      <SelectItem value="Chemistry">Chemistry</SelectItem>
                      <SelectItem value="Biology">Biology</SelectItem> */}
                          {subjects.map((subject) => (
                            <SelectItem key={subject.id} value={subject.id}>
                              {subject.subjectName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="schedule" className="w-full">
              <TabsList className="mb-2 border-b w-full justify-start rounded-none bg-transparent p-0 overflow-x-scroll overflow-y-clip">
                <TabsTrigger
                  value="manage"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 px-4 py-2"
                >
                  Manage Class
                </TabsTrigger>
                <TabsTrigger
                  value="schedule"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 px-4 py-2"
                >
                  Schedule
                </TabsTrigger>

                {isRecordTypesLoading && <LoadingSpinner />}
                {isRecordTypesError && (
                  <div className="text-red-500">Error loading record types</div>
                )}
                {recordTypesData &&
                  recordTypesData.data.length > 0 &&
                  recordTypesData.data.map((type) => (
                    <TabsTrigger
                      key={type.id}
                      value={type.name}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 px-4 py-2"
                    >
                      {type.name}
                    </TabsTrigger>
                  ))}
              </TabsList>

              <TabsContent value="schedule" className="mt-0">
                <ClassScheduleOverview
                  classId={classId}
                  setClassSchedule={setClassSchedule}
                />
              </TabsContent>

              <TabsContent value="Attendance">
                <div className="divide-y divide-gray-200 border border-gray-200 rounded-t-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="divide-x divide-gray-200">
                        <TableHead className="font-medium text-xs text-gray-500">
                          NAME
                        </TableHead>
                        <TableHead className="font-medium text-xs text-gray-500 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Checkbox
                              id="mark-all-attendance"
                              checked={markAllAttendance}
                              onCheckedChange={handleMarkAllAttendance}
                              className="data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                            />
                            <label
                              htmlFor="mark-all-attendance"
                              className="text-xs font-medium text-gray-500"
                            >
                              ATTENDANCE
                            </label>
                          </div>
                        </TableHead>
                        <TableHead className="font-medium text-xs text-gray-500">
                          COMMENT
                        </TableHead>
                        <TableHead className="font-medium text-xs text-gray-500"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedSubject === undefined ? (
                        <div className="text-center py-8 w-full">
                          <p> Please select a subject first</p>
                        </div>
                      ) : (
                        <>
                          {filteredEnrollments.map((enrollment) => (
                            <AttendanceRowData
                              student={enrollment.child}
                              handleAttendanceChange={handleAttendanceChange}
                              handleCommentChange={handleCommentChange}
                            />
                          ))}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      Rows per page:
                    </span>
                    <Select defaultValue="10">
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue placeholder="10" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-1">
                    <div className="text-sm text-gray-500">1-10 of 276</div>
                    <div className="flex">
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="manage">
                {filteredEnrollments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      No students have been enrolled yet.
                    </p>
                    <Button
                      className="bg-purple-600 hover:bg-purple-700"
                      onClick={handleOpenModal}
                    >
                      Add your first student
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 border border-gray-200 rounded-t-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="divide-x divide-gray-200">
                          <TableHead className="font-medium text-xs text-gray-500">
                            First Name
                          </TableHead>
                          <TableHead className="font-medium text-xs text-gray-500">
                            Last Name
                          </TableHead>
                          <TableHead className="font-medium text-xs text-gray-500">
                            Parent
                          </TableHead>
                          <TableHead className="font-medium text-xs text-gray-500">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEnrollments.map((enrollment) => (
                          <StudentRowData
                            key={enrollment.childId}
                            childId={enrollment.childId}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* <TabsContent value="assignment">
                <div className="divide-y divide-gray-200 border border-gray-200 rounded-t-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="divide-x divide-gray-200">
                        <TableHead className="font-medium text-xs text-gray-500">
                          NAME
                        </TableHead>
                        <TableHead className="font-medium text-xs text-gray-500">
                          GRADE
                        </TableHead>
                        <TableHead className="font-medium text-xs text-gray-500 w-1/2">
                          COMMENT
                        </TableHead>
                        <TableHead className="font-medium text-xs text-gray-500"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignmentData.map((student) => (
                        <TableRow
                          key={student.id}
                          className="hover:bg-gray-50 divide-x divide-gray-200"
                        >
                          <TableCell>
                            <div className="flex items-center">
                              <Avatar className="mr-2">
                                <AvatarImage
                                  src={student.avatar || '/placeholder.svg'}
                                  alt={student.name}
                                />
                                <AvatarFallback>
                                  {student.name.substring(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <span>{student.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={student.grade}
                                onChange={(e) =>
                                  handleGradeChange(
                                    student.id,
                                    Number(e.target.value)
                                  )
                                }
                                className="w-16 text-center border border-gray-300 rounded-md"
                              />
                              <span className="ml-2">%</span>
                            </div>
                          </TableCell>
                          <TableCell className="w-1/2">
                            <Textarea
                              placeholder="Enter a Comment..."
                              className="min-h-[80px] resize-none"
                              value={student.comment}
                              onChange={(e) =>
                                handleAssignmentCommentChange(
                                  student.id,
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Open menu</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  View Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  View Submission
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  Send Feedback
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  Grant Extension
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                
                <div className="flex items-center justify-between pt-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      Rows per page:
                    </span>
                    <Select defaultValue="10">
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue placeholder="10" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-1">
                    <div className="text-sm text-gray-500">1-10 of 276</div>
                    <div className="flex">
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent> */}

              {recordTypesData &&
                recordTypesData.data.length > 0 &&
                recordTypesData.data.map((type) => {
                  if (type.name.toLowerCase() === 'attendance') {
                    return <></>;
                  }
                  return (
                    <TabsContent value={type.name}>
                      <div className="divide-y divide-gray-200 border border-gray-200 rounded-t-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="divide-x divide-gray-200">
                              <TableHead className="font-medium text-xs text-gray-500">
                                NAME
                              </TableHead>
                              <TableHead className="font-medium text-xs text-gray-500">
                                MARKS
                              </TableHead>
                              <TableHead className="font-medium text-xs text-gray-500">
                                GRADE
                              </TableHead>
                              <TableHead className="font-medium text-xs text-gray-500 w-1/2">
                                COMMENT
                              </TableHead>
                              <TableHead className="font-medium text-xs text-gray-500"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedSubject === undefined ? (
                              <div className="text-center py-8 w-full">
                                <p> Please select a subject first</p>
                              </div>
                            ) : (
                              <>
                                {filteredEnrollments.map((enrollment) => (
                                  <StudentRecordRowData
                                    key={enrollment.id}
                                    enrollment={enrollment}
                                    recordTypeId={type.id}
                                  />
                                ))}
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between pt-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">
                            Rows per page:
                          </span>
                          <Select defaultValue="10">
                            <SelectTrigger className="w-[70px] h-8">
                              <SelectValue placeholder="10" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center space-x-1">
                          <div className="text-sm text-gray-500">
                            1-10 of 276
                          </div>
                          <div className="flex">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  );
                })}
            </Tabs>
          </div>

          <div className="lg:col-span-1">
            {/* Performance Section */}
            <div className="bg-white rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium">Performance</h2>
                <div className="text-4xl font-bold text-green-500">85%</div>
              </div>

              {/* Subject Progress Bars */}
              <div className="space-y-4">
                {[
                  { subject: 'Math', progress: 30 },
                  { subject: 'English', progress: 90 },
                  { subject: 'Yoruba', progress: 65 },
                  { subject: 'Civic Education', progress: 80 },
                  { subject: 'Physics', progress: 60 },
                ].map((subject) => (
                  <div key={subject.subject} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{subject.subject}</span>
                    </div>
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-600 rounded-full"
                        style={{ width: `${subject.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sm flex items-center gap-1"
                >
                  Last 7 Days
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Grade Section */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-lg font-medium mb-6">Grade</h2>
              <div className="flex justify-center">
                <div className="relative w-40 h-40">
                  <div className="absolute inset-0 rounded-full border-8 border-green-100"></div>
                  <div
                    className="absolute inset-0 rounded-full border-8 border-green-500 border-t-transparent border-r-transparent border-b-transparent"
                    style={{ transform: 'rotate(-45deg)' }}
                  ></div>
                  <div className="absolute inset-4 bg-green-200 rounded-full flex items-center justify-center">
                    <span className="text-3xl font-bold text-green-800">
                      7.5
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for creating new class */}
      <ClassModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
}
